import React, { FunctionComponent } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { CheckCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Login from './components/Login/Login';
import { Register } from '~/pages/Auth/components/Register/Register';
import { ForgotPassword } from '~/pages/Auth/components/ForgotPassword/ForgotPassword';
import { ResetPassword } from '~/pages/Auth/components/ResetPassword/ResetPassword';
import { SignWithLink } from '~/pages/Auth/components/SignWithLink/SignWithLink';
import { VerifyEmail } from '~/pages/Auth/components/VerifyEmail/VerifyEmail';
import LoginByEmail from '~/pages/Auth/components/LoginByEmail/LoginByEmail';
import logoBlack from '../../assets/icons/logo-black.png';
import ImpersonationModal from '~/components/ImpersonationModal/ImpersonationModal';
import { APP_URL, APP_NAME } from '@akademiasaas/shared';

const BLUE = '#2b45c9';
const BLUE_DK = '#1e2f8f';
const MUTE = '#8a8f9c';
const YELLOW = '#f5c518';

interface OwnProps {}

type Props = OwnProps;

const Auth: FunctionComponent<Props> = () => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const showImpersonateModal = searchParams.get('impersonate') === 'true';
  const [isOpen, setIsOpen] = React.useState(showImpersonateModal);

  const showTabs = ['/auth/login', '/auth/register'].includes(location.pathname);
  const activeTab = location.pathname === '/auth/register' ? 'register' : 'login';

  const features = [
    t<string>('brand.features.photo'),
    t<string>('brand.features.feedback'),
    t<string>('brand.features.students'),
  ];

  return (
    <>
      <div className="flex min-h-screen bg-white overflow-x-hidden">
        {/* Brand panel — desktop only */}
        <div
          className="hidden lg:flex flex-col relative overflow-hidden"
          style={{
            flex: '0 0 42%',
            maxWidth: 520,
            background: `linear-gradient(160deg, ${BLUE} 0%, ${BLUE_DK} 100%)`,
            color: '#fff',
            padding: '56px 48px',
          }}
        >
          <div
            className="absolute rounded-full"
            style={{ width: 340, height: 340, background: 'rgba(255,255,255,0.06)', top: -120, right: -120 }}
          />
          <div
            className="absolute rounded-full"
            style={{ width: 220, height: 220, background: 'rgba(245,197,24,0.14)', bottom: -70, left: -60 }}
          />
          <div className="relative flex justify-center">
            <a href={APP_URL} target="_blank" rel="nofollow noreferrer">
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  borderRadius: 16,
                  padding: '16px 28px',
                }}
              >
                <img src={logoBlack} alt={APP_NAME} className="h-16 w-auto block" />
              </span>
            </a>
          </div>
          <div className="relative mt-auto">
            <h2 style={{ fontWeight: 700, fontSize: 30, lineHeight: 1.2, margin: '0 0 16px', color: '#fff' }}>
              {t<string>('brand.headline')}
            </h2>
            <p style={{ fontSize: 15.5, opacity: 0.82, lineHeight: 1.6, margin: 0, maxWidth: 380 }}>
              {t<string>('brand.description')}
            </p>
            <div className="flex flex-col" style={{ gap: 12, marginTop: 30 }}>
              {features.map((feature) => (
                <div key={feature} className="flex items-center" style={{ gap: 10, fontSize: 15 }}>
                  <CheckCircleFilled style={{ color: YELLOW, fontSize: 18 }} /> {feature}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Form side */}
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center py-8 px-4 sm:px-10">
          <div className="w-full" style={{ maxWidth: 420 }}>
            <div className="lg:hidden flex justify-center mb-6">
              <a href={APP_URL} target="_blank" rel="nofollow noreferrer">
                <img src={logoBlack} alt={APP_NAME} className="h-20 w-auto block" />
              </a>
            </div>

            {showTabs && (
              <div
                className="flex w-full"
                style={{ background: '#f2f3f8', borderRadius: 12, padding: 4, marginBottom: 24 }}
              >
                {(
                  [
                    ['login', t<string>('tabs.login'), '/auth/login'],
                    ['register', t<string>('tabs.register'), '/auth/register'],
                  ] as const
                ).map(([key, label, path]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => history.push(path)}
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      border: 'none',
                      fontFamily: 'inherit',
                      borderRadius: 9,
                      padding: '10px 0',
                      fontWeight: 700,
                      fontSize: 14.5,
                      background: activeTab === key ? '#fff' : 'transparent',
                      color: activeTab === key ? BLUE : MUTE,
                      boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all .15s',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <Switch>
              <Route exact path="/auth/forgot-password/:email?" component={ForgotPassword} />
              <Route exact path="/auth/login" component={Login} />
              <Route exact path="/auth/register" component={Register} />
              <Route exact path="/auth/reset-password" component={ResetPassword} />
              <Route exact path="/auth/verify-email" component={VerifyEmail} />
              <Route exact path="/auth/sign-with-link" component={SignWithLink} />
              <Route exact path="/auth/login-by-link" component={LoginByEmail} />
              <Redirect from="/" exact to="/auth/login" />
              <Redirect from="/auth" exact to="/auth/login" />
            </Switch>
          </div>
        </div>
      </div>
      <ImpersonationModal open={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default Auth;
